// src/helpers/schedule/genericSchedule.js
import { HOURS_PER_DAY, sum } from './config';

/**
 * Build WBS from a generic schedule backbone.
 * - Parents are placed by their DECLARED dependencies (finish-to-start),
 *   falling back to the schedule start when a row declares none.
 *
 *   This used to walk a date cursor and place every row after the one
 *   before it by Id - a "FS-1 chain". The backbone JSON declares real
 *   dependencies on 29 of its 31 rows, so that flattened a network into
 *   a single serial line: work that runs in parallel was drawn one after
 *   another, and the plan was longer than the plan said it was.
 * - Children AWP tasks are created from awp_codes and filled with meshes/hours/costs.
 *
 * @param {Array} scheduleRows              rows from schedule.json
 * @param {Date} startDate                  start of the whole schedule
 * @param {Object<string,Array>} meshRowsByAwp  { AWP: [{meshId,laborHours,equipHours,totalCost}, ...] }
 * @param {Object<string,Object>} awpSummaryByCode  e.g. { AWP: { total, tolerancePct } }
 * @returns {Array} WBS array (parents with children)
 */
export function buildWbsFromGenericSchedule(
  scheduleRows,
  startDate,
  meshRowsByAwp,
  awpSummaryByCode
) {
  if (!Array.isArray(scheduleRows)) return [];

  const rows = scheduleRows
    .slice()
    .sort((a, b) => Number(a.Id) - Number(b.Id));

  const scheduleStart = new Date(startDate || new Date());

  const addDays = (d, n) => {
    const r = new Date(d);
    r.setDate(r.getDate() + Number(n || 0));
    return r;
  };

  // Declared predecessors, in the parents' own id space. The JSON names
  // them by row Id ("2"), which is not the id the WBS uses ("S-2").
  const idOf = (rowId) => 'S-' + String(rowId);
  const predsOf = (r) => (Array.isArray(r.dependencies) ? r.dependencies : [])
    .map((d) => idOf(typeof d === 'object' && d !== null ? (d.Id != null ? d.Id : d.id) : d))
    .filter(Boolean);

  // Finish dates as they are settled, so a row can be placed from its
  // predecessors rather than from whatever came before it in the file.
  const endById = new Map();
  const wbs = [];

  for (let i = 0; i < rows.length; i++) {
    const r = rows[i] || {};
    const duration = Math.max(1, Number(r.duration_days || 0));

    // Where this row can start: after the LATEST of its declared
    // predecessors finishes. A row that declares none starts at the
    // schedule start - that is what the backbone's first two rows say,
    // and they really are concurrent.
    const preds = predsOf(r);
    let cursor = new Date(scheduleStart);
    for (const pid of preds) {
      const end = endById.get(pid);
      // A predecessor further down the file has not been placed yet; the
      // pass below settles it.
      if (end && end > cursor) cursor = new Date(end);
    }

    // Parent = schedule row
    let parent = {
      id: 'S-' + String(r.Id),
      predecessorIds: preds,
      wbsCode: String(r.Code ?? r.Id ?? i + 1),
      name: String(r.task || `Task ${r.Id}`),
      type: 'schedule',
      category: null,
      startDate: new Date(cursor),
      endDate: addDays(cursor, duration),
      duration,
      progress: 0,
      level: 1,
      color: '#6c7a89',
      visible: true,
      expanded: true,
      totalPrice: 0,
      isAWP: false,
      awpCode: null,
      awpCodes: Array.isArray(r.awp_codes) ? r.awp_codes.slice() : [],
      dependencies: Array.isArray(r.dependencies) ? r.dependencies.slice() : [],
      meshIds: [],
      children: []
    };

    // Children = AWP codes (skip "N/A")
    const awpCodes = (Array.isArray(r.awp_codes) ? r.awp_codes : []).filter(
      (x) => x && x !== 'N/A'
    );

    if (awpCodes.length > 0) {
      let prevEnd = new Date(parent.startDate);

      awpCodes.forEach((code, j) => {
        const rowsForCode = (meshRowsByAwp && meshRowsByAwp[code]) || [];

        const laborH = sum(rowsForCode.map((m) => Number(m.laborHours || 0)));
        const equipH = sum(rowsForCode.map((m) => Number(m.equipHours || 0)));
        const totalH = laborH + equipH;

        const summary = (awpSummaryByCode && awpSummaryByCode[code]) || {};
        const costFromSummary = Number(summary.total || 0);
        const costFromMeshes = sum(rowsForCode.map((m) => Number(m.totalCost || 0)));
        const childCost = costFromSummary > 0 ? costFromSummary : costFromMeshes;

        // duration: prefer hours; else split parent evenly
        const childDays =
          totalH > 0
            ? Math.max(1, Math.ceil(totalH / HOURS_PER_DAY))
            : Math.max(1, Math.ceil(parent.duration / awpCodes.length));

        const childStart = new Date(prevEnd);
        const childEnd = addDays(childStart, childDays);

        const child = {
          id: parent.id + '-AWP-' + code,
          wbsCode: parent.wbsCode + '.' + String(j + 1),
          name: 'AWP ' + code,
          type: 'awp',
          category: null,
          startDate: childStart,
          endDate: childEnd,
          duration: childDays,
          progress: 0,
          level: 2,
          color: '#ff6b6b',
          visible: true,
          totalPrice: childCost,
          laborHours: laborH,
          equipmentHours: equipH,
          isAWP: true,
          awpCode: code,
          tolerancePct:
            typeof summary.tolerancePct === 'number' ? summary.tolerancePct : 0,
          meshIds: rowsForCode.map((m) => m.meshId)
        };

        parent.children.push(child);
        parent.meshIds = parent.meshIds.concat(child.meshIds);
        parent.totalPrice += Number(child.totalPrice || 0);
        prevEnd = new Date(child.endDate);
      });

      // span parent across its children
      parent.startDate = new Date(parent.children[0].startDate);
      parent.endDate = new Date(parent.children[parent.children.length - 1].endDate);
      parent.duration = Math.max(
        1,
        Math.ceil((parent.endDate - parent.startDate) / (1000 * 60 * 60 * 24))
      );
    }

    wbs.push(parent);
    endById.set(parent.id, new Date(parent.endDate));
  }

  // A row may declare a predecessor that appears LATER in the file, which
  // the single pass above could not honour. Settle those by re-placing
  // until nothing moves. Bounded by the row count, so a circular
  // declaration stops rather than spins.
  for (let pass = 0; pass < wbs.length; pass++) {
    let moved = false;
    for (const parent of wbs) {
      if (!parent.predecessorIds.length) continue;
      let want = new Date(scheduleStart);
      for (const pid of parent.predecessorIds) {
        const end = endById.get(pid);
        if (end && end > want) want = new Date(end);
      }
      if (+want === +parent.startDate) continue;
      const shift = Math.round((want - parent.startDate) / 86400000);
      if (!shift) continue;
      parent.startDate = want;
      parent.endDate = addDays(parent.endDate, shift);
      for (const child of parent.children) {
        child.startDate = addDays(child.startDate, shift);
        child.endDate = addDays(child.endDate, shift);
      }
      endById.set(parent.id, new Date(parent.endDate));
      moved = true;
    }
    if (!moved) break;
  }

  return wbs;
}
